import React from "react";
import { Link } from "react-router-dom";
import { BellRinging, RssSimple, Heart, ShieldCheck, ShareNetwork, GithubLogo } from "@phosphor-icons/react";
import { useCounty } from "../lib/CountyContext";
import { useAuth } from "../lib/AuthContext";
import { trackDonateClick } from "../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SiteFooter() {
  const { county } = useCounty();
  const { user } = useAuth();
  const rssUrl = `${BACKEND_URL}/api/feeds/incidents.rss?county=${county || "okanogan"}`;
  const icsUrl = `${BACKEND_URL}/api/feeds/incidents.ics?county=${county || "okanogan"}`;
  const reporterTarget = user
    ? user.verified ? null : "/apply-reporter"
    : "/signup?next=/apply-reporter";

  const handleShare = async () => {
    const data = {
      title: "Okanogan Highlands Firewatch",
      text: "Free live wildfire dashboard for Washington — dispatch, satellite hotspots, scanner audio, evac alerts.",
      url: "https://okanoganhighlandsfirewatch.app",
    };
    try {
      if (navigator.share) {
        await navigator.share(data);
      } else {
        await navigator.clipboard.writeText(data.url);
        alert("Link copied to clipboard.");
      }
    } catch { /* user dismissed */ }
  };

  return (
    <footer
      data-testid="site-footer"
      className="border-t border-tac-border bg-tac-surface/30 mt-8"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
          <FooterCol
            icon={BellRinging}
            title="Get Alerts"
            desc="Push notifications when a fire enters your saved area."
            cta="Set up alerts"
            to="/"
            testid="footer-alerts"
          />
          {reporterTarget && (
            <FooterCol
              icon={ShieldCheck}
              title="Become a Reporter"
              desc="Verified locals post field updates with a gold shield."
              cta="Apply now"
              to={reporterTarget}
              testid="footer-reporter"
            />
          )}
          <FooterColExternal
            icon={RssSimple}
            title="RSS / Calendar"
            desc="Subscribe to incident feeds for your county."
            cta="Get the feed"
            href={rssUrl}
            secondaryHref={icsUrl}
            secondaryLabel="iCal"
            testid="footer-rss"
          />
          <FooterCol
            icon={Heart}
            title="Keep It Free"
            desc="One-time or recurring — every dollar covers servers + APIs."
            cta="Donate"
            to="/donate"
            testid="footer-donate"
            onCtaClick={() => trackDonateClick("footer")}
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-tac-border/60">
          <div className="text-[11px] font-mono-tac text-tac-faint leading-relaxed max-w-2xl">
            Okanogan Highlands Firewatch is community-run, ad-free, and not
            affiliated with any government agency. Always cross-reference with
            official sources for evacuation decisions.
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleShare}
              data-testid="footer-share"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider border border-tac-border bg-tac-surface hover:bg-tac-surface2 hover:border-tac-primary text-tac-text rounded-sm transition-colors"
              title="Share Firewatch"
            >
              <ShareNetwork size={11} weight="bold" /> Share
            </button>
            <Link
              to="/reporters"
              className="text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 text-tac-dim hover:text-tac-text"
              data-testid="footer-reporters-link"
            >
              Reporters
            </Link>
            <Link
              to="/about"
              className="text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 text-tac-dim hover:text-tac-text"
              data-testid="footer-about-link"
            >
              About
            </Link>
            <Link
              to="/whats-new"
              className="text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 text-tac-dim hover:text-tac-text"
              data-testid="footer-whats-new-link"
            >
              What's New
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ icon: Icon, title, desc, cta, to, testid, onCtaClick }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon size={14} weight="duotone" className="text-tac-primary" />
        <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-tac-text">
          {title}
        </span>
      </div>
      <p className="text-[12px] text-tac-dim leading-relaxed mb-2">{desc}</p>
      <Link
        to={to}
        onClick={onCtaClick}
        className="text-[10px] font-bold uppercase tracking-wider text-tac-primary hover:underline"
      >
        {cta} →
      </Link>
    </div>
  );
}

function FooterColExternal({ icon: Icon, title, desc, cta, href, secondaryHref, secondaryLabel, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon size={14} weight="duotone" className="text-tac-primary" />
        <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-tac-text">
          {title}
        </span>
      </div>
      <p className="text-[12px] text-tac-dim leading-relaxed mb-2">{desc}</p>
      <div className="flex gap-3">
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[10px] font-bold uppercase tracking-wider text-tac-primary hover:underline"
        >
          {cta} →
        </a>
        {secondaryHref && (
          <a
            href={secondaryHref}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] font-bold uppercase tracking-wider text-tac-dim hover:text-tac-text"
          >
            {secondaryLabel} →
          </a>
        )}
      </div>
    </div>
  );
}
